import React from 'react';
import { Button, Drawer, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { ReactComponent as IncognitoIcon } from 'new/components/icons/incognito.svg';
import { impersonate } from 'js/actions';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { ChipBadge } from 'new/elements/ChipBadge';
import { useFetchAdminOrg } from 'new/stores/adminOrgs/adminOrgs.api';
import { ORG_STATUS_CHIP_COLOR, ORG_STATUS_LABEL, orgInitial } from '../utils';
import { RoleBanner } from '../RoleBanner';
import { CustomerOrgTabs } from './CustomerOrgTabs';
import styles from './CustomerOrgDrawer.module.scss';

interface CustomerOrgDrawerProps {
  orgId: number | null;
  canWrite: boolean;
  writeArmed: boolean;
  onToggleWriteArmed: (armed: boolean) => void;
  onClose: () => void;
}

export function CustomerOrgDrawer({
  orgId,
  canWrite,
  writeArmed,
  onToggleWriteArmed,
  onClose,
}: CustomerOrgDrawerProps) {
  const { data: org } = useFetchAdminOrg(orgId ?? 0, { skip: !orgId });
  const dispatch = useAppDispatch();
  const currentUser = useCurrentUser();
  const role = canWrite && writeArmed ? 'full' : 'readonly';

  const isSelfOwner =
    !!org?.owner && (currentUser?.email === org.owner.email || currentUser?.id === org.owner.id);
  const canImpersonateOwner = canWrite && !!org?.owner?.id && !isSelfOwner;

  const onImpersonateOwner = () => {
    if (!org?.owner?.id) return;
    dispatch(impersonate(org.owner.id, org.id));
    onClose();
  };

  return (
    <Drawer keepMounted={false} open={!!orgId} anchor="right" onClose={onClose}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.header}>
            {org ? (
              <div className={styles.headerInfo}>
                <div className={styles.avatar}>{orgInitial(org.name)}</div>
                <div>
                  <div className={styles.headerTitleRow}>
                    <Typography className={styles.headerName} variant="h6">
                      {org.name}
                    </Typography>
                    <span className={styles.idBadge}>org_{org.id}</span>
                    <ChipBadge
                      dot
                      color={ORG_STATUS_CHIP_COLOR[org.status]}
                      label={ORG_STATUS_LABEL[org.status]}
                    />
                    <ChipBadge color="primary" label={org.org_tier?.display_name ?? '—'} />
                  </div>
                  <div className={styles.headerSubline}>
                    {org.email_domain} · Owner {org.owner?.full_name || org.owner?.email || '—'} ·
                    Created {new Date(org.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ) : (
              <Stack direction="row" spacing={2} alignItems="center">
                <Skeleton variant="rounded" width={48} height={48} />
                <Skeleton variant="text" width={200} height={32} />
              </Stack>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
              {canImpersonateOwner && (
                <Button
                  variant="outlined"
                  startIcon={<IncognitoIcon />}
                  onClick={onImpersonateOwner}
                >
                  Impersonate owner
                </Button>
              )}
              <IconButton onClick={onClose} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Stack>
          </div>

          {org && (
            <div className={styles.roleBannerWrap}>
              <RoleBanner
                canWrite={canWrite}
                writeArmed={writeArmed}
                onToggleWriteArmed={onToggleWriteArmed}
              />
            </div>
          )}

          {org ? (
            // Deliberately not gated on isFetching too — every write action in this drawer
            // (plan change, trial extend/end, tier limit save, member updates) invalidates
            // this org's cache tag and triggers a background refetch. Gating on isFetching
            // would unmount CustomerOrgTabs on every one of those, wiping its local
            // activeTab state and dumping the admin back on the Overview tab mid-edit.
            <CustomerOrgTabs org={org} role={role} />
          ) : (
            <div className={styles.emptyState}>Loading…</div>
          )}
        </div>
      </div>
    </Drawer>
  );
}
